import { Grant, VestingFrequency, VestingSchedule } from '../../../../generated/graphql'

export function calculateVestedOptions(
    grantDate: Date,
    numberGranted: number,
    vestingFrequency: VestingFrequency,
    cliffMonths: number = 0,
    totalVestingMonths: number = 0,
    currentDate: Date = new Date()
): number {

  if (numberGranted <= 0 || cliffMonths < 0 || totalVestingMonths < 0) {
    return 0
  }
  if (currentDate < grantDate) {
    return 0
  }

  if (totalVestingMonths === 0) {
    const monthsElapsed = calculateMonthsElapsed(grantDate, currentDate)
    return monthsElapsed >= cliffMonths ? numberGranted : 0
  }

  const timeElapsed = calculateTimeElapsed(grantDate, currentDate, vestingFrequency!)
  const cliffPeriod = convertMonthsToFrequency(cliffMonths, vestingFrequency!)
  const totalVestingPeriods = convertMonthsToFrequency(totalVestingMonths, vestingFrequency!)

  if (timeElapsed < cliffPeriod) {
    return 0
  }

  if (timeElapsed >= totalVestingPeriods) {
    return numberGranted
  }

  const vestingProgress = timeElapsed / totalVestingPeriods
  const vestedOptions = Math.floor(numberGranted * vestingProgress)

  return Math.min(vestedOptions, numberGranted)
}


function calculateMonthsElapsed(startDate: Date, endDate: Date): number {
  const yearDiff = endDate.getFullYear() - startDate.getFullYear()
  const monthDiff = endDate.getMonth() - startDate.getMonth()
  const dayDiff = endDate.getDate() - startDate.getDate()

  let totalMonths = yearDiff * 12 + monthDiff

  if (dayDiff < 0) {
    totalMonths -= 1
  }

  return Math.max(0, totalMonths)
}


function calculateTimeElapsed(
  startDate: Date,
  endDate: Date,
  frequency: VestingFrequency | null
): number {
  if (!frequency) {
    return calculateMonthsElapsed(startDate, endDate)
  }

  const timeDiffMs = endDate.getTime() - startDate.getTime()
  const monthsElapsed = calculateMonthsElapsed(startDate, endDate)

  switch (frequency) {
    case 'Daily':
      return Math.floor(timeDiffMs / (1000 * 60 * 60 * 24))

    case 'Weekly':
      return Math.floor(timeDiffMs / (1000 * 60 * 60 * 24 * 7))

    case 'Monthly':
      return monthsElapsed

    case 'Quarterly':
      return Math.floor(monthsElapsed / 3)

    case 'Semi_Annually':
      return Math.floor(monthsElapsed / 6)

    case 'Annually':
      return Math.floor(monthsElapsed / 12)

    default:
      return monthsElapsed
  }
}


function convertMonthsToFrequency(months: number, frequency: VestingFrequency | null): number {
  if (!frequency || months === 0) {
    return months
  }

  switch (frequency) {
    case 'Daily':
      return Math.floor(months * 30.44)

    case 'Weekly':
      return Math.floor(months * 4.33)

    case 'Monthly':
      return months

    case 'Quarterly':
      return Math.floor(months / 3)

    case 'Semi_Annually':
      return Math.floor(months / 6)

    case 'Annually':
      return Math.floor(months / 12)

    default:
      return months
  }
}
